import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { getUserForumActivity } from '../../../services/firebase/forum';
import { useTheme } from '../../../context/ThemeContext';
import LoadingSkeleton from '../../common/LoadingSkeleton';
import ActivityStats from './ActivityStats';
import RecentActivityList from './RecentActivityList';

/**
 * ForumActivity component for displaying user's forum participation
 * Shows questions asked, replies given, and engagement stats
 */
const ForumActivity = ({ userId, className = '' }) => {
  const { isDarkMode } = useTheme();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchActivity = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await getUserForumActivity(userId, { limit: 5 });
      
      if (result.success) {
        setActivity(result.data);
        setRetryCount(0); // Reset retry count on success
      } else {
        console.error('Forum activity fetch error:', result);
        setError(result.error || 'Failed to load forum activity');
      }
    } catch (err) {
      console.error('Error fetching forum activity:', err);
      setError('Failed to load forum activity');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchActivity();
  };

  useEffect(() => {
    let isMounted = true;

    const fetchActivitySafe = async () => {
      if (!userId) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        
        const result = await getUserForumActivity(userId, { limit: 5 });
        
        if (isMounted) {
          if (result.success) {
            setActivity(result.data);
            setRetryCount(0);
          } else {
            console.error('Forum activity fetch error:', result);
            setError(result.error || 'Failed to load forum activity');
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching forum activity:', err);
          setError('Failed to load forum activity');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchActivitySafe();

    return () => {
      isMounted = false;
    };
  }, [userId]);



  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center mb-4">
          <ChatBubbleLeftRightIcon className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Forum Activity
          </h2>
        </div>
        
        {/* Loading Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="text-center">
              <LoadingSkeleton className="h-8 w-12 mx-auto mb-2" />
              <LoadingSkeleton className="h-3 w-16 mx-auto" />
            </div>
          ))}
        </div>
        
        {/* Loading Activity Items */}
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <LoadingSkeleton key={index} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center mb-4">
          <ChatBubbleLeftRightIcon className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Forum Activity
          </h2>
        </div>
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
            Unable to load forum activity
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mb-3">
            Please try again or refresh the page
          </p>
          {retryCount < 3 && (
            <button
              onClick={handleRetry}
              className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium transition-colors duration-200"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const { stats, recentActivity } = activity || {};

  if (!stats || stats.totalActivity === 0) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center mb-4">
          <ChatBubbleLeftRightIcon className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Forum Activity
          </h2>
        </div>
        <div className="text-center py-8">
          <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No forum activity yet
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
            This user hasn't asked questions or replied in the forum
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center mb-4">
        <ChatBubbleLeftRightIcon className="w-6 h-6 mr-3 text-purple-600 dark:text-purple-400" />
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
          Forum Activity
        </h2>
      </div>

      {/* Stats Summary */}
      <ActivityStats stats={stats} />

      {/* Recent Activity */}
      {recentActivity && recentActivity.length > 0 && (
        <RecentActivityList 
          activities={recentActivity} 
          totalActivity={stats.totalActivity}
        />
      )}
    </div>
  );
};

export default ForumActivity;